import { useEffect, useRef, useState } from 'react';
import { m, useInView } from 'motion/react';
import { Building2, Home, Trees, Sparkles } from 'lucide-react';

function CountUp({ target, suffix = '', duration = 2000 }: { target: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: false, margin: '0px 0px -80px 0px' });

  useEffect(() => {
    if (!inView) {
      setCount(0);
      return;
    }
    const startTime = performance.now();
    let raf: number;
    const animate = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(animate);
      else setCount(target);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);

  const formatted = target >= 1000 ? count.toLocaleString('tr-TR') : String(count);

  return (
    <div ref={ref} className="text-5xl font-bold text-white mb-2">
      {formatted}{suffix}
    </div>
  );
}

export function About() {
  const features = [
    {
      icon: Building2,
      title: 'Modern Mimari',
      description: 'Çağdaş tasarım anlayışı ile inşa edilen premium konut yapıları',
    },
    {
      icon: Home,
      title: 'Konforlu Yaşam',
      description: 'Her detayı düşünülmüş, konforlu ve ferah yaşam alanları',
    },
    {
      icon: Trees,
      title: 'Yeşil Alanlar',
      description: 'Doğayla iç içe, geniş yeşil alanlar ve peyzaj düzenlemeleri',
    },
    {
      icon: Sparkles,
      title: 'Prestijli Lokasyon',
      description: "İstanbul'un en prestijli bölgesinde, ulaşım avantajlı konum",
    },
  ];

  return (
    <section id="about" className="py-24 bg-white dark:bg-slate-950">
      <div className="container mx-auto px-4 lg:px-8">
        <m.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full text-sm tracking-wider mb-4">
            PROJE HAKKINDA
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-slate-100 mb-6">
            Yaşam Kalitenizi{' '}
            <span className="text-stone-700 dark:text-stone-300">
              Yükseltin
            </span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Rain Nova, modern mimari anlayışını prestijli yaşamla buluşturan, İstanbul'un
            en yenilikçi konut projesidir. Geniş yeşil alanları, zengin sosyal donatıları
            ve stratejik konumuyla ailenize hak ettiği yaşam kalitesini sunar.
          </p>
        </m.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {features.map((feature, index) => (
            <m.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="text-center group"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white group-hover:bg-slate-900 dark:group-hover:bg-white group-hover:text-white dark:group-hover:text-slate-900 transition-all duration-300 group-hover:scale-110">
                <feature.icon size={28} />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">{feature.description}</p>
            </m.div>
          ))}
        </div>

        <m.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 bg-gradient-to-br from-slate-900 to-slate-900 rounded-2xl p-12 text-white"
        >
          <div className="text-center">
            <CountUp target={110} duration={1800} />
            <div className="text-gray-300">Toplam Konut</div>
          </div>
          <div className="text-center border-l border-r border-gray-700">
            <CountUp target={11} duration={1400} />
            <div className="text-gray-300">Kat Sayısı</div>
          </div>
          <div className="text-center">
            <CountUp target={15200} suffix="m²" duration={2200} />
            <div className="text-gray-300">İnşaat Alanı</div>
          </div>
        </m.div>
      </div>
    </section>
  );
}
